import { DashboardLayout } from "components/DashboardLayout";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { InvoiceHeader } from "components/design/InvoiceHeader";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { Grid } from "components/icons/Grid";
import { InvoicePreivew } from "components/InvoicePreivew/InvoicePreivew";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHistory, useParams } from "react-router-dom";
import BillsIcon from "static/images/BillsIcon.png";
import Copy from "static/images/Copy.svg";
import OnboardingCelebration from "static/images/OnboardingCelebration.svg";
import {
  useCreateEntityInvoiceMutation,
  useGetEntityInvoiceQuery,
  useGenrateShortLinkMutation,
} from "store/apis/invoices";
import { InvoiceStatus } from "./InvoiceFullPageView";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";

export const InvoiceSendPage = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const { alertToast, successToast } = useToast();

  const { data: invoice, isLoading } = useGetEntityInvoiceQuery(
    {
      groupId,
      entityId,
      invoiceId,
    },
    { skip: !groupId || !entityId || !invoiceId }
  );

  const [createInvoice, { isLoading: creatingInvoice }] =
    useCreateEntityInvoiceMutation();

  const [generateLinkToken] = useGenrateShortLinkMutation();

  const copyInvoiceLink = async () => {
    try {
      const { token } = await generateLinkToken({
        entityId,
        invoiceId,
        from: "mail_draft_screen",
      }).unwrap();
      const link = `${window.location.origin}/invoice/${token}`;
      await navigator.clipboard.writeText(link);
      successToast({ message: "Invoice link has been copied" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="t-flex t-h-full t-w-full t-justify-center t-items-center">
        <Loader />
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const onBackClick = () => {
    history.replace(link(`/books/invoices-and-customers/${invoiceId}`));
  };

  const goToInvoices = () => {
    history.push(link(`/books/invoices-and-customers`));
  };

  const createNewInvoice = async () => {
    try {
      const newInvoice = await createInvoice({
        groupId,
        entityId,
      }).unwrap();

      history.replace(
        link(
          `/books/invoices-and-customers/${newInvoice.uuid}?entity=${entityId}&company=${groupId}`
        )
      );
    } catch (error) {
      console.error("Failed to create invoice:", error);
    }
  };

  const invoiceHeader = (
    <InvoiceHeader
      status={
        <InvoiceStatus
          invoiceStatus={invoice.status}
          pendingAmount={invoice.pending_amount}
        />
      }
      title={invoice.invoice_number}
      onBackClick={onBackClick}
    />
  );

  return (
    <div className="t-fixed t-z-modal t-top-0 t-left-0 t-w-full t-h-full t-bg-surface t-grid t-grid-cols-2">
      <div className="t-overflow-auto t-pt-6 t-shadow-light-100">
        <DashboardLayout header={invoiceHeader}>
          <div className="t-mt-5 t-p-10 t-flex t-flex-col t-gap-6">
            <div className="t-text-heading-lg t-font-semibold t-m-0 t-items-center">
              Invoice has been successfully created and sent!{" "}
              <img
                src={OnboardingCelebration}
                alt="OnboardingCelebration"
                className="t-size-5 t-ml-1"
              />
            </div>
            <div className="t-border t-border-solid t-rounded-lg t-border-neutral-0 t-p-4">
              <div className="t-bg-purple-0 t-p-4 t-relative t-overflow-hidden t-rounded-lg">
                <div className="t-absolute t-inset-0 t-opacity-50">
                  <Grid />
                </div>
                <div className="t-flex t-justify-between t-gap-1 t-relative t-z-10">
                  <div className="t-flex t-flex-col t-gap-4">
                    <span className="t-text-body-sm t-text-text-30">
                      {invoice.sent_to}
                    </span>
                    <span className="t-text-subtitle-lg t-font-semibold t-text-text-100">
                      <AmountSuperScript amount={Number(invoice.sub_total)} />
                    </span>
                  </div>
                  <img
                    src={BillsIcon}
                    alt="OnboardingCelebration"
                    className="t-size-20"
                  />
                </div>
              </div>
              <div className="t-flex t-flex-col t-gap-3 t-mt-7 t-ml-4">
                {invoice.due_date && (
                  <div className="t-flex t-flex-col t-gap-1">
                    <span className="t-text-text-30 t-text-body-sm">
                      Due on
                    </span>
                    <span className="t-text-body-sm t-text-text-100">
                      {dayjs(invoice.due_date).format(DISPLAY_DATE)}
                    </span>
                  </div>
                )}
                {invoice.customer?.recipient_emails && (
                  <div className="t-flex t-flex-col t-gap-1">
                    <span className="t-text-body-sm t-text-text-30">
                      Invoice sent to
                    </span>
                    <span className="t-text-body-sm t-text-text-100">
                      {invoice.customer?.recipient_emails?.join(",")}
                    </span>
                  </div>
                )}

                <div className="t-flex t-flex-col t-gap-1">
                  <span className="t-text-body-sm t-text-text-30">
                    Invoice Link
                  </span>
                  <span className="t-text-body-sm t-text-text-100">
                    <button
                      className="all:unset t-flex t-items-center t-gap-2 t-cursor-pointer"
                      type="button"
                      onClick={copyInvoiceLink}
                    >
                      <span>{`${window.location.origin}/invoice/.....`}</span>
                      <img src={Copy} alt="copy icon" className="copyIcon" />
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </DashboardLayout>
      </div>
      <div className="t-overflow-auto t-pt-6 t-bg-surface-lighter-grey">
        <DashboardLayout
          header={
            <div className="t-flex t-gap-2 t-justify-end t-bg-surface-lighter-grey t-items-center">
              <Button size="small" onClick={goToInvoices}>
                Go to Invoices
              </Button>
              <Button
                size="small"
                customType="primary"
                onClick={createNewInvoice}
                isLoading={creatingInvoice}
                disabled={creatingInvoice}
              >
                Create new invoice
              </Button>
            </div>
          }
        >
          <InvoicePreivew invoice={invoice} />
        </DashboardLayout>
      </div>
    </div>
  );
};
